import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { progressReviewApi } from '../api/progress-reviews';
import { apprenticeApi } from '../api/apprentices';
import axios from 'axios';
import { Apprentice } from '../types';

const REVIEW_TYPES = [
  'Initial',
  'Quarterly',
  'Mid-point',
  'Final',
  'Ad-hoc',
];

const KSB_RATINGS = [
  { value: 'not-started', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'developing', label: 'Developing', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'competent', label: 'Competent', color: 'bg-blue-100 text-blue-800' },
  { value: 'proficient', label: 'Proficient', color: 'bg-green-100 text-green-800' },
];

export const CreateProgressReview: React.FC = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [apprentices, setApprentices] = useState<Apprentice[]>([]);
  const [selectedApprentice, setSelectedApprentice] = useState<Apprentice | null>(null);
  const [standard, setStandard] = useState<any>(null);
  const [ksbProgress, setKsbProgress] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showKSBs, setShowKSBs] = useState(false);
  
  const [formData, setFormData] = useState({
    apprentice_id: '',
    review_date: new Date().toISOString().split('T')[0],
    review_type: 'Quarterly',
    progress_summary: '',
    targets_set: '',
  });

  useEffect(() => {
    const loadApprentices = async () => {
      if (!user?.tenant_id) return;

      try {
        setLoading(true);
        const instructorEmail = userRole === 'instructor' ? user.email : undefined;
        const response = await apprenticeApi.getAll(
          user.tenant_id,
          undefined,
          instructorEmail
        );
        setApprentices(response.data);
      } catch (err) {
        console.error('Failed to load apprentices:', err);
        setError('Failed to load apprentices');
      } finally {
        setLoading(false);
      }
    };

    loadApprentices();
  }, [user?.tenant_id, user?.email, userRole]);

  useEffect(() => {
    const loadStandard = async () => {
      if (!selectedApprentice?.standard_id || !user?.tenant_id) return;

      try {
        const response = await axios.get(`/api/standards/${selectedApprentice.standard_id}`);
        setStandard(response.data);
        
        // Initialize KSB progress with empty ratings
        const initialProgress: any = {
          knowledge: {},
          skills: {},
          behaviours: {},
        };
        
        response.data.knowledge?.forEach((_: any, index: number) => {
          initialProgress.knowledge[`K${index + 1}`] = 'not-started';
        });
        response.data.skills?.forEach((_: any, index: number) => {
          initialProgress.skills[`S${index + 1}`] = 'not-started';
        });
        response.data.behaviours?.forEach((_: any, index: number) => {
          initialProgress.behaviours[`B${index + 1}`] = 'not-started';
        });
        
        setKsbProgress(initialProgress);
      } catch (err) {
        console.error('Failed to load standard:', err);
      }
    };

    loadStandard();
  }, [selectedApprentice, user?.tenant_id]);

  const handleApprenticeChange = (apprenticeId: string) => {
    const apprentice = apprentices.find(a => a.id === apprenticeId);
    setSelectedApprentice(apprentice || null);
    setFormData({ ...formData, apprentice_id: apprenticeId });
  };

  const handleKSBRating = (category: string, ksbId: string, rating: string) => {
    setKsbProgress({
      ...ksbProgress,
      [category]: {
        ...ksbProgress[category],
        [ksbId]: rating,
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.tenant_id) return;

    try {
      setSaving(true);
      setError('');

      await progressReviewApi.create({
        tenant_id: user.tenant_id,
        apprentice_id: formData.apprentice_id,
        review_date: formData.review_date,
        review_type: formData.review_type,
        progress_summary: formData.progress_summary,
        targets_set: formData.targets_set,
        ksb_progress: showKSBs ? ksbProgress : null,
        reviewer_id: user.user_id,
      });

      navigate('/progress-reviews');
    } catch (err: any) {
      console.error('Failed to create review:', err);
      setError(err.response?.data?.message || 'Failed to create progress review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Progress Review</h1>
        <p className="text-gray-600">Record apprentice progress and set development targets</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Apprentice Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apprentice *
              </label>
              <select
                required
                value={formData.apprentice_id}
                onChange={(e) => handleApprenticeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select Apprentice --</option>
                {apprentices.map(apprentice => (
                  <option key={apprentice.id} value={apprentice.id}>
                    {apprentice.first_name} {apprentice.last_name} - {apprentice.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Review Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Date *
              </label>
              <input
                type="date"
                required
                value={formData.review_date}
                onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Review Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Type *
              </label>
              <select
                required
                value={formData.review_type}
                onChange={(e) => setFormData({ ...formData, review_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {REVIEW_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Progress Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Progress Summary
              </label>
              <textarea
                rows={6}
                value={formData.progress_summary}
                onChange={(e) => setFormData({ ...formData, progress_summary: e.target.value })}
                placeholder="Summarise the apprentice's progress, achievements, and areas for development..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Targets Set */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Targets for Next Period
              </label>
              <textarea
                rows={6}
                value={formData.targets_set}
                onChange={(e) => setFormData({ ...formData, targets_set: e.target.value })}
                placeholder="Set SMART targets for the apprentice to work towards..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* KSB Progress Toggle */}
            {selectedApprentice && standard && (
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">KSB Progress Ratings</h3>
                    <p className="text-sm text-gray-600">Rate apprentice progress on each Knowledge, Skill, and Behaviour</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowKSBs(!showKSBs)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    {showKSBs ? 'Hide KSBs' : 'Rate KSBs'}
                  </button>
                </div>

                {showKSBs && (
                  <div className="space-y-6">
                    {/* Skills */}
                    {standard.skills?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Skills ({standard.skills.length})</h4>
                        <div className="space-y-3">
                          {standard.skills.map((skill: any, index: number) => (
                            <div key={index} className="p-3 bg-green-50 rounded-lg">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <span className="inline-block px-2 py-1 bg-green-600 text-white rounded text-xs font-semibold mr-2">
                                    S{index + 1}
                                  </span>
                                  <span className="text-sm text-gray-700">{skill.detail}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-2">
                                {KSB_RATINGS.map(rating => (
                                  <button
                                    key={rating.value}
                                    type="button"
                                    onClick={() => handleKSBRating('skills', `S${index + 1}`, rating.value)}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition ${
                                      ksbProgress.skills?.[`S${index + 1}`] === rating.value
                                        ? rating.color + ' ring-2 ring-offset-1 ring-green-600'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    {rating.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Knowledge */}
                    {standard.knowledge?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Knowledge ({standard.knowledge.length})</h4>
                        <div className="space-y-3">
                          {standard.knowledge.map((item: any, index: number) => (
                            <div key={index} className="p-3 bg-blue-50 rounded-lg">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <span className="inline-block px-2 py-1 bg-blue-600 text-white rounded text-xs font-semibold mr-2">
                                    K{index + 1}
                                  </span>
                                  <span className="text-sm text-gray-700">{item.detail}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-2">
                                {KSB_RATINGS.map(rating => (
                                  <button
                                    key={rating.value}
                                    type="button"
                                    onClick={() => handleKSBRating('knowledge', `K${index + 1}`, rating.value)}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition ${
                                      ksbProgress.knowledge?.[`K${index + 1}`] === rating.value
                                        ? rating.color + ' ring-2 ring-offset-1 ring-blue-600'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    {rating.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Behaviours */}
                    {standard.behaviours?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Behaviours ({standard.behaviours.length})</h4>
                        <div className="space-y-3">
                          {standard.behaviours.map((item: any, index: number) => (
                            <div key={index} className="p-3 bg-purple-50 rounded-lg">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <span className="inline-block px-2 py-1 bg-purple-600 text-white rounded text-xs font-semibold mr-2">
                                    B{index + 1}
                                  </span>
                                  <span className="text-sm text-gray-700">{item.detail}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-2">
                                {KSB_RATINGS.map(rating => (
                                  <button
                                    key={rating.value}
                                    type="button"
                                    onClick={() => handleKSBRating('behaviours', `B${index + 1}`, rating.value)}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition ${
                                      ksbProgress.behaviours?.[`B${index + 1}`] === rating.value
                                        ? rating.color + ' ring-2 ring-offset-1 ring-purple-600'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    {rating.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate('/progress-reviews')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {saving ? 'Creating...' : 'Create Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
